<!--
File:                   CategoriesView.vue
Author:                 Vincent Audergon (https://github.com/AudergonV)
Description:            Categories view page
                        Allows to create, delete categories and assign/deassign drawings
Version:                1.1
Last modification date: 14.08.26
-->

<template>
  <div class="categories">
    <h1>Catégories</h1>

    <!-- Actions -->
    <v-card class="elevation-1">
      <v-card-text>
        <v-label>Actions</v-label>
        <v-row class="mt-1">
          <v-col cols="12">
            <v-btn small color="primary" dark @click="openCreateDialog">
              <v-icon dark>mdi-plus</v-icon>
              Ajouter une catégorie
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row class="mt-3">
      <!-- Categories list -->
      <v-col cols="12" md="4" lg="3">
        <v-card class="elevation-1">
          <v-card-title>Liste des catégories</v-card-title>
          <v-list dense>
            <v-list-item
              v-for="category in categories"
              :key="category.uuid"
              @click="selectCategory(category)"
              :class="selectedCategory && selectedCategory.uuid === category.uuid ? 'accent lighten-4' : ''"
            >
              <v-list-item-content>
                <v-list-item-title>{{ category.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ getDrawingCount(category) }} dessin(s)</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click.stop="openDeleteDialog(category)">
                  <v-icon color="error">mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-card-text v-if="categories.length == 0">Aucune catégorie</v-card-text>
        </v-card>
      </v-col>

      <!-- Drawings of the selected category -->
      <v-col cols="12" md="8" lg="9">
        <v-card v-if="selectedCategory" class="elevation-1">
          <v-card-title>
            <span>Dessins de la catégorie "{{ selectedCategory.name }}"</span>
            <v-spacer></v-spacer>
            <v-btn small color="success" dark @click="openAddDialog">
              <v-icon dark>mdi-plus</v-icon>
              Ajouter des dessins
            </v-btn>
          </v-card-title>
          <v-data-table
            :items="selectedCategory.drawings"
            :headers="headers"
            item-key="uuid"
            class="elevation-1"
            :loading="drawingsLoading"
            loading-text="Chargement des dessins..."
            :items-per-page="-1"
            hide-default-footer
          >
            <template v-slot:item.png="{ item }">
              <v-img class="mt-1 mb-1" :src="item.png" height="100" width="100"></v-img>
            </template>
            <template v-slot:item.creationDate="{ item }">
              {{ moment(item.creationDate).format("DD/MM/YYYY") }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn small color="error" outlined @click="removeDrawing(item)">
                ❌ Retirer
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
        <v-card v-else class="elevation-1">
          <v-card-text>Sélectionnez une catégorie pour voir ses dessins.</v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create category dialog -->
    <v-dialog v-model="createDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="text-h5">Nouvelle catégorie</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newCategoryName"
            label="Nom de la catégorie"
            @keyup.enter="createCategory"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="createDialog = false; newCategoryName = ''">Annuler</v-btn>
          <v-btn color="success" text :disabled="!newCategoryName" @click="createCategory">Créer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete category dialog -->
    <v-dialog v-model="deleteDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="text-h5">⚠️ Supprimer la catégorie</v-card-title>
        <v-card-text class="text-justify">
          Supprimer la catégorie <b><u>{{ categoryToDelete ? categoryToDelete.name : '' }}</u></b> ? Les dessins qu'elle contient ne seront pas supprimés.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" text @click="deleteDialog = false">Annuler</v-btn>
          <v-btn color="error" text @click="deleteCategory">Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add drawings dialog -->
    <v-dialog v-model="addDialog" persistent max-width="900" content-class="add-dialog">
      <v-card class="add-dialog-card">
        <v-card-title class="text-h5">
          Ajouter des dessins à "{{ selectedCategory ? selectedCategory.name : '' }}"
        </v-card-title>
        <v-divider></v-divider>
        <div class="add-drawings-list">
          <v-progress-linear v-if="addLoading" indeterminate class="ma-2"></v-progress-linear>
          <template v-else-if="addableDrawings.length > 0">
            <div
              v-for="drawing in addableDrawings"
              :key="drawing.uuid"
              class="drawing-row"
              :class="{ 'drawing-selected': isDrawingSelected(drawing) }"
              @click="toggleDrawing(drawing)"
            >
              <img class="drawing-row__thumb" :src="drawing.png" width="100" height="100" alt="">
              <div class="drawing-row__text">
                <div class="drawing-row__name">{{ drawing.name }}</div>
                <div class="drawing-row__subtitle">{{ drawing.uuid }}</div>
              </div>
              <v-checkbox
                class="drawing-row__check"
                :input-value="isDrawingSelected(drawing)"
                @click.stop="toggleDrawing(drawing)"
              ></v-checkbox>
            </div>
          </template>
          <v-card-text v-else>Aucun dessin disponible</v-card-text>
        </div>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeAddDialog">Annuler</v-btn>
          <v-btn color="success" text :disabled="drawingsToAdd.length == 0" @click="addDrawings">
            Ajouter ({{ drawingsToAdd.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading -->
    <v-overlay :value="waiting">
      <v-row>
        <v-col cols="12" class="text-center">
          <v-progress-circular
            :size="70"
            :width="7"
            color="accent"
            indeterminate
          ></v-progress-circular>
        </v-col>
        <v-col cols="12" class="text-center">
          <span class="title">{{ waitingMessage }}</span>
        </v-col>
        <v-col cols="12" class="text-center">
          <span class="title">{{ waitingMessage2 }}</span>
        </v-col>
      </v-row>
    </v-overlay>
  </div>
</template>

<script>
import api from "@/api";
import moment from "moment";

export default {
    data() {
        return {
            moment,
            categories: [],
            selectedCategory: null,
            drawingsLoading: false,
            waiting: false,
            waitingMessage: "",
            waitingMessage2: "",
            createDialog: false,
            newCategoryName: "",
            deleteDialog: false,
            categoryToDelete: null,
            addDialog: false,
            addLoading: false,
            addableDrawings: [],
            drawingsToAdd: [],
            headers: [
                { text: 'Aperçu', value: 'png', sortable: false },
                { text: 'Nom', value: 'name' },
                { text: 'ID', value: 'uuid' },
                { text: 'Date de publication', value: 'creationDate' },
                { text: 'Actions', value: 'actions', sortable: false },
            ],
        };
    },
    created() {
        this.loadData();
    },
    methods: {
        async loadData() {
            this.categories = (await api.getCategories()).map(category => {
                category.drawings = [];
                category.loaded = false;
                return category;
            });
            if (this.categories.length > 0) {
                await this.selectCategory(this.categories[0]);
            } else {
                this.selectedCategory = null;
            }
        },
        async loadCategoryDrawings(category) {
            this.drawingsLoading = true;
            let drawings = [];
            let from = 0;
            let to = 10;
            let d = [];
            do {
                d = await api.getCategoryDrawings(category.uuid, from, to);
                drawings.push(...d);
                from += 10;
                to += 10;
            } while (d.length >= 10);
            category.drawings = drawings;
            category.loaded = true;
            if (this.selectedCategory && this.selectedCategory.uuid === category.uuid) {
                this.drawingsLoading = false;
            }
        },
        async selectCategory(category) {
            this.selectedCategory = category;
            if (!category.loaded) {
                await this.loadCategoryDrawings(category);
            }
        },
        getDrawingCount(category) {
            if (category.loaded) {
                return category.drawings.length;
            }
            return category.drawingCount != null ? category.drawingCount : 0;
        },
        openCreateDialog() {
            this.newCategoryName = "";
            this.createDialog = true;
        },
        async createCategory() {
            if (!this.newCategoryName) {
                return;
            }
            await api.createCategory(this.newCategoryName);
            this.createDialog = false;
            this.newCategoryName = "";
            await this.loadData();
        },
        openDeleteDialog(category) {
            this.categoryToDelete = category;
            this.deleteDialog = true;
        },
        async deleteCategory() {
            if (!this.categoryToDelete) {
                return;
            }
            await api.deleteCategory(this.categoryToDelete.uuid);
            this.deleteDialog = false;
            if (this.selectedCategory && this.selectedCategory.uuid === this.categoryToDelete.uuid) {
                this.selectedCategory = null;
            }
            this.categoryToDelete = null;
            await this.loadData();
        },
        async openAddDialog() {
            this.drawingsToAdd = [];
            this.addLoading = true;
            this.addableDrawings = await api.getAddableDrawings(this.selectedCategory.uuid);
            this.addLoading = false;
            this.addDialog = true;
        },
        closeAddDialog() {
            this.addDialog = false;
            this.drawingsToAdd = [];
        },
        isDrawingSelected(drawing) {
            return this.drawingsToAdd.some(d => d.uuid === drawing.uuid);
        },
        toggleDrawing(drawing) {
            const index = this.drawingsToAdd.findIndex(d => d.uuid === drawing.uuid);
            if (index >= 0) {
                this.drawingsToAdd.splice(index, 1);
            } else {
                this.drawingsToAdd.push(drawing);
            }
        },
        async addDrawings() {
            this.addDialog = false;
            this.waitingMessage = "Ajout des dessins...";
            this.waitingMessage2 = `Cette opération peut prendre un certain temps, veuillez patienter.`;
            this.waiting = true;
            let total = 1;
            for (let drawing of this.drawingsToAdd) {
                this.waitingMessage2 = `Ajout de ${drawing.name} (${total}/${this.drawingsToAdd.length})`;
                await api.addDrawingToCategory(this.selectedCategory.uuid, drawing.uuid);
                total++;
            }
            this.drawingsToAdd = [];
            await this.loadCategoryDrawings(this.selectedCategory);
            this.waiting = false;
            this.waitingMessage = "";
            this.waitingMessage2 = "";
        },
        async removeDrawing(drawing) {
            await api.removeDrawingFromCategory(this.selectedCategory.uuid, drawing.uuid);
            this.selectedCategory.drawings = this.selectedCategory.drawings.filter(d => d.uuid !== drawing.uuid);
        },
    },
};
</script>

<style scoped>
.add-dialog {
    overflow: hidden;
}

.add-dialog-card {
    display: flex;
    flex-direction: column;
    height: 85vh;
    max-height: 85vh;
}

.add-drawings-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
}

.drawing-row {
    display: flex;
    align-items: center;
    padding: 16px;
    cursor: pointer;
}

.drawing-row:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.drawing-selected {
    background-color: rgba(0, 128, 0, 0.08);
}

.drawing-row__thumb {
    flex-shrink: 0;
    border-radius: 4px;
    margin-right: 12px;
}

.drawing-row__text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
}

.drawing-row__name {
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.drawing-row__subtitle {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.drawing-row__check {
    flex-shrink: 0;
    margin: 0;
}
</style>
